import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PageContainer, ErrorBoundary, Skeleton } from '@whinerp/ui';
import { useT } from '../app/i18n.js';
import { SessionProvider, useSession, useSessionQuery } from '../app/session.js';
import { useBrandPalette } from '../app/brand.js';
import { NavRail } from './NavRail.js';
import { TopBar } from './TopBar.js';
import { GlobalSearch } from './GlobalSearch.js';
import { CommandPalette } from './CommandPalette.js';
import { NotificationPanel } from './NotificationPanel.js';
import { VisitBanner } from './VisitBanner.js';
import { SandboxBadge } from './SandboxBadge.js';
import { WorkspaceSandboxBanner } from './WorkspaceSandboxBanner.js';
import { UsageBanner } from './UsageBanner.js';

/**
 * The shell every authenticated screen sits inside.
 *
 * It reads the session once and renders nothing until it has it: navigation
 * built from a half-loaded entitlement set would show an area for a moment and
 * then take it away, which is the flicker that makes a product feel unsure of
 * itself.
 *
 * Each region has its own error boundary. A failed nav rail should not take the
 * content with it.
 */

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const query = useSessionQuery();

  if (query.isPending) return <ShellSkeleton />;
  if (query.isError || !query.data) {
    // Not signed in is the ordinary case here, and the router sends them to
    // sign in. Anything else is a real failure and says so.
    return <ShellUnavailable status={query.error?.status ?? 0} message={query.error?.message ?? ''} />;
  }

  return (
    <SessionProvider session={query.data}>
      <ShellChrome>{children}</ShellChrome>
    </SessionProvider>
  );
}

function ShellChrome({ children }: { children: ReactNode }): JSX.Element {
  const [searchOpen, setSearchOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Cmd/Ctrl+K anywhere. The palette and search are separate surfaces because
  // one finds records and the other runs actions, and merging them makes both
  // worse.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (event.key === '/' && !isTypingTarget(event.target)) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleRail = useCallback(() => setCollapsed((value) => !value), []);
  const { pathname } = useLocation();

  /*
   * The workspace wears its own colour from here (F-015).
   *
   * Inside the shell rather than in a provider higher up, because it needs the
   * session AND the resolved theme, and it has to run before the first screen
   * paints: applying it later means the product renders in its own colours and
   * then changes them, which reads as a bug on every page load.
   */
  const { session } = useSession();
  useBrandPalette(session.workspace.brandPalette ?? null, session.workspace.brandingEnabled);

  return (
    <div data-component="AppShell" className="flex min-h-screen flex-col bg-canvas text-ink">
      {/*
        * Above everything, including the navigation, and outside every error
        * boundary: a banner saying somebody is watching must not be the thing
        * that fails to draw. It returns null when there is no visit, so the
        * ordinary case costs a row of nothing.
        */}
      <VisitBanner />

      {/* Which providers are not real (part 3). Drawn once, above everything,
          and only when something is in sandbox or unconfigured. */}
      <SandboxBadge />

      {/* This workspace is a sandbox, not production (G-287). */}
      <WorkspaceSandboxBanner />

      <div className="flex min-h-0 flex-1">
      <ErrorBoundary region="Navigation">
        <NavRail collapsed={collapsed} onToggle={toggleRail} />
      </ErrorBoundary>

      <div className="flex min-w-0 flex-1 flex-col">
        <ErrorBoundary region="The top bar">
          <TopBar
            onOpenSearch={() => setSearchOpen(true)}
            onOpenPalette={() => setPaletteOpen(true)}
            onOpenNotifications={() => setNotificationsOpen(true)}
          />
        </ErrorBoundary>

        {/* Before a limit stops somebody, not after (F-143). */}
        <UsageBanner />

        {/*
          * One container, every screen.
          *
          * 32px above the title and 48px below the last card, so a page ends
          * rather than stopping. Each screen used to decide this for itself
          * and most decided nothing, which is why content sat in a
          * fixed-width block at the top left of a wide window.
          */}
        <main className="flex-1 px-gutter pb-7 pt-6">
          <PageContainer>
            {/* Keyed on the path: navigating away is a person asking for
                something else, and they are owed an attempt at it rather than
                the last screen's failure. */}
            <ErrorBoundary region="This screen" resetKey={pathname}>
              {children}
            </ErrorBoundary>
          </PageContainer>
        </main>
      </div>

      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <NotificationPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
    </div>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * The shell's own loading state.
 *
 * Shaped like the shell it becomes - rail, top bar, content - so nothing moves
 * when the session arrives.
 */
function ShellSkeleton(): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen bg-canvas" role="status" aria-busy aria-live="polite">
      <span className="sr-only">{t('state.loading')}</span>
      <div className="w-rail-expanded shrink-0 border-e border-e-rule bg-surface p-3">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-touch w-full" />
          ))}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-topbar items-center gap-3 border-b border-b-rule bg-surface px-4">
          <Skeleton className="h-input w-reading max-w-full" />
        </div>
        <div className="mx-auto w-full max-w-content px-gutter py-5">
          <Skeleton className="h-line-h1 w-block-xl" />
        </div>
      </div>
    </div>
  );
}

function ShellUnavailable({ status, message }: { status: number; message: string }): JSX.Element {
  const t = useT();
  const signedOut = status === 401;
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="flex max-w-reading flex-col items-start gap-3">
        <h1 className="text-h3 font-medium text-ink">
          {signedOut ? t('auth.signin.title') : t('state.error')}
        </h1>
        <p className="text-body text-ink-meta">{signedOut ? t('error.forbidden') : message || t('error.unexpected')}</p>
        <a
          href="/sign-in"
          className="inline-flex h-btn items-center rounded-control bg-bronze px-btn-x text-body font-medium text-bronze-on"
        >
          {t('action.signIn')}
        </a>
      </div>
    </div>
  );
}
